import { useState } from 'react';
import { Badge, Button, Collapse, Modal, Paper, Stack, Table, Tooltip } from '@mantine/core';
import { IconChevronDown, IconChevronRight, IconFlask, IconPlus } from '@tabler/icons-react';
import { EmptyState } from '../../../components/EmptyState';
import { PageHeader } from '../../../components/PageHeader';
import { useSites, useStages } from '../api';
import { RUN_MODE_LABELS } from '../format';
import type { StageDto } from '../types';
import { RecipesPanel } from './RecipesPanel';
import { StageForm } from './StageForm';

function StageRow({ stage, siteName }: { stage: StageDto; siteName: string }) {
  const [expanded, setExpanded] = useState(false);
  const [editOpened, setEditOpened] = useState(false);

  return (
    <>
      <Table.Tr>
        <Table.Td>
          <Button
            variant="subtle"
            size="compact-xs"
            onClick={() => setExpanded((e) => !e)}
            leftSection={expanded ? <IconChevronDown size={14} /> : <IconChevronRight size={14} />}
          >
            {stage.code}
          </Button>
        </Table.Td>
        <Table.Td>{stage.name}</Table.Td>
        <Table.Td>{siteName}</Table.Td>
        <Table.Td>
          <Badge variant="light">{RUN_MODE_LABELS[stage.runMode]}</Badge>
        </Table.Td>
        <Table.Td>{stage.isActive ? <Badge color="green">Active</Badge> : <Badge color="gray">Inactive</Badge>}</Table.Td>
        <Table.Td>
          <Button size="compact-xs" variant="subtle" onClick={() => setEditOpened(true)}>
            Edit
          </Button>
        </Table.Td>
      </Table.Tr>
      <Table.Tr>
        <Table.Td colSpan={6} p={0}>
          <Collapse in={expanded}>
            <Paper p="md" bg="var(--mantine-color-graphite-0)">
              <RecipesPanel stageId={stage.id} />
            </Paper>
          </Collapse>
        </Table.Td>
      </Table.Tr>
      <Modal opened={editOpened} onClose={() => setEditOpened(false)} title={`Edit stage — ${stage.name}`}>
        <StageForm stage={stage} onSaved={() => setEditOpened(false)} />
      </Modal>
    </>
  );
}

/** Stages &amp; recipes config (BUILD_SPEC.md §5.5, §11 Settings) — this goal's
 * sole ownership (plan.md QC fix #2). CRUD stages (SHIFT/BATCH run mode) and
 * recipes (inputs shares, outputs split%, consumables per output tonne, expected
 * yield) with activate. */
export function StagesRecipesConfig() {
  const [createOpened, setCreateOpened] = useState(false);
  const stagesQuery = useStages();
  const sitesQuery = useSites();
  const sitesById = new Map((sitesQuery.data?.items ?? []).map((s) => [s.id, s.name]));
  const stages = [...(stagesQuery.data?.items ?? [])].sort((a, b) => a.sortOrder - b.sortOrder);
  // Header/EmptyState CTA de-dupe (guidance-wave gap, 2026-08-26; found by
  // audit): on an empty register the EmptyState below already offers "New
  // stage" as the sole call-to-action — showing the same button in the page
  // header too rendered TWO "New stage" buttons on a fresh tenant. Reuses the
  // exact emptiness condition the EmptyState branch checks.
  const showHeaderCreate = stages.length > 0;

  return (
    <Stack gap="lg">
      <PageHeader
        title="Stages & recipes"
        description="Define the production stages and recipes that Record a shift and Pack conversion are built from."
        actions={
          showHeaderCreate ? (
            <Button leftSection={<IconPlus size={16} />} onClick={() => setCreateOpened(true)}>
              New stage
            </Button>
          ) : undefined
        }
      />
      <Table.ScrollContainer minWidth={640}>
        <Table verticalSpacing="xs" highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Code</Table.Th>
              <Table.Th>Name</Table.Th>
              <Table.Th>Site</Table.Th>
              <Table.Th>
                <Tooltip label="Batch: a single kiln load, cages in to cages out. Continuous: a shift's steady inputs and outputs.">
                  <span>Run mode</span>
                </Tooltip>
              </Table.Th>
              <Table.Th>Status</Table.Th>
              <Table.Th />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {stages.map((stage) => (
              <StageRow key={stage.id} stage={stage} siteName={sitesById.get(stage.siteId) ?? stage.siteId} />
            ))}
            {stages.length === 0 && (
              <Table.Tr>
                <Table.Td colSpan={6}>
                  <EmptyState
                    icon={IconFlask}
                    title="No stages configured yet"
                    hint="A stage is a step of the plant (Dryer, Hammer Mill, PM1). Create one to start defining its recipe."
                    action={
                      <Button size="xs" variant="light" leftSection={<IconPlus size={14} />} onClick={() => setCreateOpened(true)}>
                        New stage
                      </Button>
                    }
                  />
                </Table.Td>
              </Table.Tr>
            )}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>
      <Modal opened={createOpened} onClose={() => setCreateOpened(false)} title="New stage">
        <StageForm onSaved={() => setCreateOpened(false)} />
      </Modal>
    </Stack>
  );
}
